import { PropsWithChildren, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { GlobalAttributeFields, schema } from "./schema";
import { useGetFinishDataQuery, useGetSpeciesDataQuery, useGetStyleDataQuery } from "features/api/globalsApi.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { useDialog } from "hooks/useDialog.ts";

interface GlobalFormProviderProps extends PropsWithChildren<{}> {
	defaultValues?: Partial<GlobalAttributeFields>;
	isUpdate?: boolean;
	isFixingGlobalAttributes?: boolean;
	isCustomQuoteData?: boolean;
}

const GlobalFormProvider = ({
	children,
	defaultValues,
	isUpdate = false,
	isFixingGlobalAttributes = false,
	isCustomQuoteData = false
}: GlobalFormProviderProps) => {
	const [finishId, setFinishId] = useState<string>("");
	const [doorStyleId, setDoorStyleId] = useState<string>("");
	const [speciesId, setSpeciesId] = useState<string>("");
	const [productLineId, setProductLineId] = useState<string>("");
	const { dialogStatus } = useDialog();
	const { data: finishData } = useGetFinishDataQuery(
		{ styleId: doorStyleId, speciesId, finishId, productLineId },
		{ skip: !finishId }
	);
	const { data: speciesData } = useGetSpeciesDataQuery(
		{
			productLineId,
			styleId: doorStyleId,
			speciesId,
			customFinishes: dialogStatus === DialogTracking.CUSTOM_COLOR_ENTRY
		},
		{ skip: !speciesId }
	);
	const { data: doorStyleData } = useGetStyleDataQuery(
		{ styleId: doorStyleId, productLineId },
		{ skip: !doorStyleId }
	);

	const formMethods = useForm({
		mode: "onChange",
		resolver: yupResolver(schema),
		reValidateMode: "onChange",
		context: {
			hasShape: Boolean(doorStyleData?.shapes && doorStyleData.shapes.length !== 0),
			hasProfile: Boolean(doorStyleData?.cabinetProfiles && doorStyleData.cabinetProfiles.length !== 0),
			hasHinge: Boolean(doorStyleData?.hingeTypes && doorStyleData.hingeTypes.length !== 0),
			hasCase: Boolean(doorStyleData?.cases && doorStyleData.cases.length !== 0),
			hasFinish: Boolean(speciesData?.finishes && speciesData.finishes.length !== 0),
			hasPackaging: Boolean(finishData?.packagings && finishData.packagings.length !== 0),
			hasDistressing: Boolean(finishData?.distressings && finishData.distressings.length !== 0),
			hasAccentColor: Boolean(finishData?.accentColors && finishData.accentColors.length !== 0),
			hasAccentApplication: Boolean(finishData?.accentApplications?.length !== 0),
			hasConstruction: Boolean(doorStyleData?.constructions && doorStyleData.constructions.length !== 0),
			isUpdate: isUpdate,
			isFixingGlobalAttributes: isFixingGlobalAttributes,
			isCustomQuoteData: isCustomQuoteData
		}
	});

	const selectedFinish = formMethods.watch("finish");
	const selectedDoorStyle = formMethods.watch("doorStyle");
	const selectedSpecies = formMethods.watch("species");
	const selectedProductLine = formMethods.watch("productLine");

	useEffect(() => {
		if (selectedProductLine?.id) {
			setProductLineId(selectedProductLine?.id);
		}
		if (selectedFinish?.id) {
			setFinishId(selectedFinish.id);
		}

		if (selectedDoorStyle?.id) {
			setDoorStyleId(selectedDoorStyle.id);
		}

		if (selectedSpecies?.id) {
			setSpeciesId(selectedSpecies.id);
		}
	}, [selectedFinish?.id, selectedDoorStyle?.id, selectedSpecies?.id, selectedProductLine?.id]);

	return <FormProvider {...formMethods}>{children}</FormProvider>;
};

export default GlobalFormProvider;
